<template>
  <div class="carousel__stack">
    <ul class="carousel__stack__list">
      <li
        v-for="(item, idx) in items"
        :key="idx"
        ref="items"
        class="carousel__stack__item"
        :style="`--dotcom-carousel-offset: ${getOffset(idx)}`"
      >
        <slot :item="item" :is-active="isActive(idx)" />
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'SanitasCarouselStack',

  props: {
    index: {
      type: Number,
      default: 0,
    },
    items: {
      type: Array,
      default: () => [],
    },
    itemsPerPage: {
      type: Number,
      default: 1,
    },
  },

  computed: {
    realIndex() {
      return this.index * this.itemsPerPage
    },
  },

  watch: {
    realIndex(next, last) {
      let direction = next > last ? 1 : -1

      if (next === 0 && last === this.items.length - 1) {
        direction = 1
      } else if (last === 0 && next === this.items.length - 1) {
        direction = -1
      }

      let lastNodes = this.$refs.items.slice(last, last + this.itemsPerPage)
      let nextNodes = this.$refs.items.slice(next, next + this.itemsPerPage)

      // Remove the offset classes from the previous index change. If we wanted
      // to remove them manually/specifically we would have to use a timeout or
      // wait for an event. This is the simplest and most robust solution.
      this.$refs.items.forEach(item => {
        item.classList.remove(
          'carousel__stack__item--left',
          'carousel__stack__item--right',
        )
      })

      // Add the initial classes to the incoming nodes.

      if (direction < 0) {
        // Going to the LEFT
        nextNodes.forEach(item => {
          item.classList.add('carousel__stack__item--left')
        })
      } else {
        // Going to the RIGHT
        nextNodes.forEach(item => {
          item.classList.add('carousel__stack__item--right')
        })
      }

      // After the browser had time to add the classes and update the DOM we
      // set the final/intended classes to the nodes. If we add them too soon
      // the browser will apply the changes in a batch and swallow the initial
      // classes we add above. We could force a reflow but that is really bad.
      //
      // this.$nextTick doesn't work here. The transitions will still be skipped.
      //
      // The 20ms are a trial and error value. A lower value leads to a bug where
      // the transition of the incoming item is skipped.
      setTimeout(() => {
        // Switch the active class from the old to the new items.
        lastNodes.forEach(item => {
          item.classList.remove('carousel__stack__item--active')
        })
        nextNodes.forEach(item => {
          item.classList.add('carousel__stack__item--active')
        })

        if (direction < 0) {
          // Going to the LEFT
          // Move the currently visible nodes to the right.
          lastNodes.forEach(item => {
            item.classList.add('carousel__stack__item--right')
          })
          // Remove the left offset to move the new nodes into view.
          nextNodes.forEach(item => {
            item.classList.remove('carousel__stack__item--left')
          })
        } else {
          // Going to the RIGHT
          // Move the currently visible nodes to the left.
          lastNodes.forEach(item => {
            item.classList.add('carousel__stack__item--left')
          })
          // Remove the right offset to move the new nodes into view.
          nextNodes.forEach(item => {
            item.classList.remove('carousel__stack__item--right')
          })
        }
      }, 20)
    },
  },

  mounted() {
    // As this component handles the classes manually,
    // we also need to set the current active class after mounting.
    this.$refs.items.forEach((item, index) => {
      if (this.isActive(index)) {
        item.classList.add('carousel__stack__item--active')
      }
    })
  },

  methods: {
    isActive(index) {
      return (
        index >= this.realIndex &&
        index <= this.realIndex + this.itemsPerPage - 1
      )
    },
    getOffset(index) {
      return index % this.itemsPerPage
    },
  },
}
</script>

<style lang="postcss" scoped>
.carousel__stack {
  position: relative;
  width: var(--dotcom-carousel-display-width);
  height: var(--dotcom-carousel-display-height);
  overflow: hidden;

  &__list {
    list-style: none;
    height: 100%;
  }

  &__item {
    position: absolute;
    top: 0;
    left: calc(
      var(--dotcom-carousel-item-width) * var(--dotcom-carousel-offset)
    );
    width: var(--dotcom-carousel-display-width);
    height: 100%;

    display: none;
    overflow: hidden;

    transition: transform var(--dotcom-carousel-transition-duration) ease;

    &--active,
    &--left,
    &--right {
      display: flex;
    }

    &--left {
      transform: translateX(calc(var(--dotcom-carousel-display-width) * -1));
    }

    &--right {
      transform: translateX(var(--dotcom-carousel-display-width));
    }
  }
}
</style>
